import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import BrandingPanel from './BrandingPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icons (inline to avoid import issues)
const Download = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>;
const X = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>;
const DollarSign = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
const Loader2 = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>;

const PricingPdfModal = ({ isOpen, onClose, pricingData, userId }) => {
  const [pdfBranding, setPdfBranding] = useState({});
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleBrandingChange = useCallback((branding) => {
    setPdfBranding(branding);
  }, []);

  if (!isOpen || !pricingData) return null;

  const { analysis, marketData, factors, recommendations, formData } = pricingData;

  // Parse price values from strings
  const parsePrice = (priceStr) => {
    if (!priceStr) return 0;
    const cleaned = priceStr.toString().replace(/[$,]/g, '');
    return parseFloat(cleaned) || 0;
  };

  const parsePriceRange = (rangeStr) => {
    if (!rangeStr) return { min: 0, max: 0 };
    const parts = rangeStr.split(/[-–to]/);
    return {
      min: parsePrice(parts[0]),
      max: parsePrice(parts[1] || parts[0])
    };
  };

  const generatePricingPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      // Determine market trend from analysis
      const marketPosition = (analysis?.marketPosition || '').toLowerCase();
      let marketTrend = 'balanced';
      if (marketPosition.includes('seller') || marketPosition.includes('hot') || marketPosition.includes('strong')) {
        marketTrend = 'sellers';
      } else if (marketPosition.includes('buyer') || marketPosition.includes('slow') || marketPosition.includes('weak')) {
        marketTrend = 'buyers';
      } else if (marketPosition.includes('balanced') || marketPosition.includes('competitive') || marketPosition.includes('neutral')) {
        marketTrend = 'balanced';
      }
      
      // Build pricing data for PDF
      const pricingPayload = {
        neighborhood: formData?.city || formData?.neighborhood || 'Market Area',
        propertyType: formData?.propertyType || 'Residential',
        avgPrice: parsePrice(analysis?.suggestedPrice),
        priceRange: parsePriceRange(analysis?.priceRange),
        pricePerSqft: parsePrice(analysis?.pricePerSqft || marketData?.pricePerSqft),
        marketTrend: marketTrend,
        avgDaysOnMarket: parseInt(marketData?.avgDaysOnMarket) || 0,
        properties: marketData?.comparables?.map(comp => ({
          address: comp.address || 'Comparable Property',
          price: parsePrice(comp.price),
          pricePerSqft: parsePrice(comp.pricePerSqft),
          beds: parseInt(comp.beds) || 0,
          baths: parseFloat(comp.baths) || 0,
          sqft: parseInt(comp.sqft) || 0,
          status: comp.status || 'Active'
        })) || []
      };

      const response = await axios.post(`${API}/premium/pdf-export/pricing-analysis?user_id=${userId}`, {
        pricingData: pricingPayload,
        branding: pdfBranding
      });

      if (response.data.success && response.data.pdfData) {
        // Create download link
        const link = document.createElement('a');
        link.href = response.data.pdfData;
        link.download = response.data.filename || 'pricing-analysis-report.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        onClose();
      } else {
        alert(response.data.error || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-purple-600" />
              Download Pricing Report as PDF
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Summary of analysis */}
          <div className="bg-green-50 p-4 rounded-xl border border-green-200">
            <h4 className="font-semibold text-green-800 mb-3">Analysis Summary</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-green-600">Suggested Price</p>
                <p className="text-lg font-bold text-green-800">{analysis?.suggestedPrice || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-green-600">Price Range</p>
                <p className="text-lg font-bold text-green-800">{analysis?.priceRange || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-green-600">Price/SqFt</p>
                <p className="text-lg font-bold text-green-800">{analysis?.pricePerSqft || marketData?.pricePerSqft || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-green-600">Market Position</p>
                <p className="text-lg font-bold text-green-800">{analysis?.marketPosition || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Branding Panel */}
          <BrandingPanel 
            onBrandingChange={handleBrandingChange}
            initialBranding={pdfBranding}
          />

          {/* Generate Button */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={generatePricingPdf}
              disabled={isGeneratingPdf}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center gap-2"
              data-testid="pricing-generate-pdf-btn"
            >
              {isGeneratingPdf ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Generate PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPdfModal;
